// React
import React from "react";

// Third party imports
import { useDispatch, useSelector } from "react-redux";

// Redux
import { setError } from "redux/slices/snackbarSlice";
import {
  setNotificationsRead,
  setNotificationsUnread,
  setNotificationsUnreadCount,
  setLatestNotifications,
  switchToRead,
  switchToUnread,
  switchToReadLatest,
} from "redux/slices/notificationsSlice";

// APIs
import {
  listNotifications,
  listLatestNotifications,
  listReadNotifications,
  markAllNotificationsAsRead,
  updateNotification,
} from "api/notifications";

/**
 * Custom hook to manage notifications data
 * @param {Object} config - Configuration object
 * @param {number} config.activeTab - Current active tab (0 = unread, 1 = read)
 * @param {boolean} config.latestOnly - Whether to fetch only latest notifications (for popover)
 * @param {boolean} config.open - Whether popover is open (only used when latestOnly is true)
 * @param {Object} config.queryParams - Additional query parameters
 * @returns {Object} Object containing notifications data, loading states, and functions
 */
const useGetNotifications = (config = {}) => {
  const {
    activeTab = 0,
    latestOnly = false,
    open = true,
    queryParams = {},
  } = config;

  const dispatch = useDispatch();

  // Local loading states
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);

  // Redux state
  const unreadNotifications = useSelector(
    (state) => state.notifications.notificationsUnread.data
  );
  const unreadPagination = useSelector(
    (state) => state.notifications.notificationsUnread.pagination
  );
  const readNotifications = useSelector(
    (state) => state.notifications.notificationsRead.data
  );
  const readPagination = useSelector(
    (state) => state.notifications.notificationsRead.pagination
  );
  const latestNotifications = useSelector(
    (state) => state.notifications.latestNotifications
  );
  const unreadCount = useSelector(
    (state) => state.notifications.notificationsUnreadCount
  );

  // Calculate dynamic limit based on screen height (only for paginated notifications)
  const limit = React.useMemo(() => {
    if (latestOnly) return null; // Latest notifications use API default
    const screenHeight = window.innerHeight;
    const baseLimit = Math.max(4, Math.floor(screenHeight / 160));
    return Math.min(baseLimit, 20);
  }, [latestOnly]);

  // Get current notifications and pagination based on active tab and mode
  const currentNotifications = React.useMemo(() => {
    if (latestOnly) {
      return activeTab === 0 ? latestNotifications : readNotifications;
    }
    return activeTab === 0 ? unreadNotifications : readNotifications;
  }, [activeTab, latestOnly, latestNotifications, unreadNotifications, readNotifications]);

  const currentPagination = React.useMemo(() => {
    if (latestOnly) return null;
    return activeTab === 0 ? unreadPagination : readPagination;
  }, [activeTab, latestOnly, unreadPagination, readPagination]);

  const hasMore = !!currentPagination?.next;

  /**
   * Check if we need to fetch notifications from API
   * @param {boolean} reset - Whether this is a reset operation
   * @returns {boolean} - Whether we need to fetch from API
   */
  const shouldFetch = React.useMemo(() => {
    // Always fetch for latest notifications in popover mode
    if (latestOnly) {
      return true;
    }

    // For paginated notifications, check if we have data in Redux
    const currentData = activeTab === 0 ? unreadNotifications : readNotifications;
    const currentPag = activeTab === 0 ? unreadPagination : readPagination;

    // Fetch if we have no data at all
    if (currentData.length === 0) {
      return true;
    }

    // Fetch if pagination indicates there might be more data
    if (currentPag?.count > currentData.length) {
      return true;
    }

    return false;
  }, [
    latestOnly,
    activeTab,
    unreadNotifications.length,
    readNotifications.length,
    unreadPagination?.count,
    readPagination?.count
  ]);

  /**
   * Fetch notifications based on configuration
   * @param {boolean} reset - Whether to reset the list or append
   * @param {Object} customQueryParams - Custom query parameters to override config
   * @param {boolean} force - Force fetch even if Redux has data
   */
  const fetchNotifications = async (reset = true, customQueryParams = null, force = false) => {
    const params = customQueryParams || queryParams;

    // Skip API call if Redux state is sufficient and not forced
    if (!force && !shouldFetch && !latestOnly) {
      setLoading(false);
      return;
    }

    setLoading(reset);

    try {
      if (latestOnly) {
        // Fetch latest notifications (for popover)
        if (activeTab === 0) {
          const response = await listLatestNotifications(params);
          dispatch(setLatestNotifications(response.data.notifications || []));
          dispatch(setNotificationsUnreadCount(response.data.count || 0));
        } else {
          const response = await listReadNotifications(params);
          dispatch(setNotificationsRead({
            data: response.data.data || [],
            pagination: response.data.pagination || { next: null, previous: null, count: 0 }
          }));
        }
      } else {
        // Fetch paginated notifications (for main page)
        const currentData = activeTab === 0 ? unreadNotifications : readNotifications;
        const requestParams = {
          is_read: activeTab === 1,
          limit,
          offset: reset ? 0 : currentData.length,
          ...params
        };

        const response = await listNotifications(requestParams);
        const notifications = response.data.data || [];
        const pagination = response.data.pagination || {};

        if (activeTab === 0) {
          if (reset) {
            dispatch(setNotificationsUnread({ data: notifications, pagination }));
          } else {
            dispatch(setNotificationsUnread({
              data: [...unreadNotifications, ...notifications],
              pagination
            }));
          }
          // Always update the count from the API response to ensure consistency
          dispatch(setNotificationsUnreadCount(pagination.count || 0));
        } else {
          if (reset) {
            dispatch(setNotificationsRead({ data: notifications, pagination }));
          } else {
            dispatch(setNotificationsRead({
              data: [...readNotifications, ...notifications],
              pagination
            }));
          }
        }
      }
    } catch (err) {
      dispatch(setError(err.message || "Failed to fetch notifications"));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load more notifications for infinite scrolling (only for paginated mode)
   */
  const loadMoreNotifications = async () => {
    if (loadingMore || !hasMore || latestOnly) return;

    try {
      setLoadingMore(true);

      const currentOffset = activeTab === 0 ? unreadNotifications.length : readNotifications.length;
      const response = await listNotifications({
        is_read: activeTab === 1,
        limit,
        offset: currentOffset,
        ...queryParams
      });

      const newNotifications = response.data.data || [];
      const pagination = response.data.pagination || {};

      if (activeTab === 0) {
        dispatch(setNotificationsUnread({
          data: [...unreadNotifications, ...newNotifications],
          pagination
        }));
      } else {
        dispatch(setNotificationsRead({
          data: [...readNotifications, ...newNotifications],
          pagination
        }));
      }
    } catch (err) {
      dispatch(setError(err.message || "Failed to load more notifications"));
    } finally {
      setLoadingMore(false);
    }
  };

  /**
   * Handle scroll for infinite scrolling
   * @param {Event} e - Scroll event
   */
  const handleScroll = (e) => {
    if (latestOnly) return; // No infinite scroll for latest notifications

    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 100) { // 100px threshold
      loadMoreNotifications();
    }
  };

  /**
   * Mark notification as read
   * @param {Object} notification - Notification object
   */
  const markNotificationAsRead = async (notification) => {
    if (notification.is_read) return;

    try {
      // Optimistic update based on mode
      if (latestOnly) {
        dispatch(switchToReadLatest(notification.id));
      } else {
        dispatch(switchToRead(notification.id));
      }

      // Make API call
      await updateNotification(notification.id, { is_read: true });

      // If we're on read tab and this action might cause empty state, refresh immediately
      if (!latestOnly && activeTab === 1) {
        const willBeEmpty = readNotifications.length <= 1;
        const hasMoreOnServer = readPagination?.count > readNotifications.length;

        if (willBeEmpty && hasMoreOnServer) {
          await fetchNotifications(true, null, true);
        }
      }

      return true;
    } catch (err) {
      // Revert optimistic update on error
      if (!latestOnly) {
        dispatch(switchToUnread(notification.id));
      }
      dispatch(setError(err.message || "Failed to mark notification as read"));
      throw err;
    }
  };

  /**
   * Mark notification as unread
   * @param {Object} notification - Notification object
   */
  const markNotificationAsUnread = async (notification) => {
    if (!notification.is_read) return;

    try {
      // Optimistic update
      dispatch(switchToUnread(notification.id));

      // Make API call
      await updateNotification(notification.id, { is_read: false });

      // If we're on read tab and this action might cause empty state, refresh immediately
      if (!latestOnly && activeTab === 1) {
        const willBeEmpty = readNotifications.length <= 1;
        const hasMoreOnServer = readPagination?.count > 1; // Since we're removing one

        if (willBeEmpty && hasMoreOnServer) {
          await fetchNotifications(true, null, true);
        }
      }

      return true;
    } catch (err) {
      // Revert optimistic update on error
      dispatch(switchToRead(notification.id));
      dispatch(setError(err.message || "Failed to mark notification as unread"));
      throw err;
    }
  };

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();

      // Clear unread notifications and update count to 0
      dispatch(setNotificationsUnread({
        data: [],
        pagination: { next: null, previous: null, count: 0 }
      }));
      dispatch(setNotificationsUnreadCount(0));

      // Refresh to get updated data
      await fetchNotifications(true);

      return true;
    } catch (err) {
      dispatch(setError(err.message || "Failed to mark all notifications as read"));
      throw err;
    }
  };

  // Auto-fetch for popover only
  React.useEffect(() => {
    if (latestOnly && open) {
      fetchNotifications(true);
    }
  }, [activeTab, open]);

  /**
   * Manually refresh notifications
   */
  const refreshNotifications = () => {
    fetchNotifications(true);
  };

  return {
    // Data
    currentNotifications,
    unreadNotifications,
    readNotifications,
    latestNotifications,
    unreadCount,

    // Pagination (only relevant for paginated mode)
    pagination: currentPagination,
    hasMore,
    limit,

    // Loading states
    loading,
    loadingMore,

    // Configuration
    activeTab,
    latestOnly,

    // Fetch functions
    fetchNotifications,
    loadMoreNotifications,
    refreshNotifications,

    // Action functions
    markNotificationAsRead,
    markNotificationAsUnread,
    markAllAsRead,

    // Utility functions
    handleScroll,
  };
};

export default useGetNotifications;
